import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cltw } from '../../utils';
import { TinyCheckmark } from '../../icons/icons/Tiny/TinyCheckmark';

export type CheckboxVariant = 'normal' | 'dark';

const checkboxVariants = {
  normal: 'border-black data-[state=checked]:bg-black',
  dark: 'border-white data-[state=checked]:bg-white',
};

export interface CheckboxProps {
  defaultChecked?: boolean | 'indeterminate';
  checked?: boolean | 'indeterminate';
  onCheckedChange?: (checked: boolean | 'indeterminate') => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  variant?: CheckboxVariant;
}

export interface CheckboxPrimitiveProps extends CheckboxProps {
  className?: string;
}

/**
 * @description Base checkbox component
 * @documentaion https://ui.shadcn.com/docs/components/checkbox
 */
const BasicCheckbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxPrimitiveProps
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    className={cltw(
      'border-primary focus-visible:ring-ring data-[state=checked]:text-primary-foreground peer h-6 w-6 shrink-0 items-center justify-center rounded-sm border text-center focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    ref={ref}
    {...props}
  >
    <CheckboxPrimitive.Indicator className={cltw('flex')}>
      <TinyCheckmark
        fill={props.variant === 'dark' ? 'black' : 'white'}
        height={14}
        strokeWidth={2}
        width={14}
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
BasicCheckbox.displayName = 'BasicCheckbox';

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ ...props }, ref) => {
  return (
    <BasicCheckbox
      className={cltw(
        checkboxVariants[props.variant || 'normal'],
        'flex rounded-md border-2',
      )}
      ref={ref}
      variant={props.variant}
      {...props}
    />
  );
});
Checkbox.displayName = 'Checkbox';

export { BasicCheckbox, Checkbox };
